import { useCallback, useEffect, useMemo, useState } from "react";
import {
  Stack,
  Group,
  Stepper,
  Paper,
  Alert,
  Text,
  Anchor,
  Code,
  Badge,
} from "@mantine/core";
import {
  IconFilter,
  IconBraces,
  IconCloudUpload,
  IconAlertTriangle,
  IconInfoCircle,
} from "@tabler/icons-react";
import { useQuery, useMutation } from "@tanstack/react-query";
import { Link } from "react-router-dom";
import { notifications } from "@mantine/notifications";
import { grInvoiceAutomationApi } from "../../../api/grInvoiceAutomation.api";
import { apiError } from "../../../api/client";
import { useAuth } from "../../../auth/AuthContext";
import { FilterPanel } from "./FilterPanel";
import { ResultsTable } from "./ResultsTable";
import { JsonReview } from "./JsonReview";
import { DuvoPushPanel } from "./DuvoPushPanel";
import { todayStr, weekAgoStr } from "./helpers";

const DEFAULT_FILTERS = () => ({
  from: weekAgoStr(),
  to: todayStr(),
  supplierCodes: [],
  branchCodes: [],
  limit: 200,
  excludeFresh: true,
});

/**
 * Sample data generation wizard — search SAP GRs, generate the Duvo-ready
 * JSON, and push the selected GRs to a Duvo sandbox. Lives inside the
 * `gr-invoice-automation` module page as one of two tabs.
 */
export function SampleDataTab() {
  const { user } = useAuth();
  const canPush =
    user?.isAdmin ||
    !!user?.modulePermissions?.["gr-invoice-automation"]?.push;

  const [step, setStep] = useState(0);
  const [filters, setFilters] = useState(DEFAULT_FILTERS);
  const [headers, setHeaders] = useState([]);
  const [selected, setSelected] = useState(new Set());
  const [generated, setGenerated] = useState(null);
  const [reviewSelected, setReviewSelected] = useState(new Set());

  useEffect(() => {
    if (generated?.payload) {
      setReviewSelected(
        new Set(generated.payload.map((g) => g.transaction_key))
      );
    } else {
      setReviewSelected(new Set());
    }
  }, [generated]);

  const reviewSelectedPayload = useMemo(
    () =>
      (generated?.payload || []).filter((g) =>
        reviewSelected.has(g.transaction_key)
      ),
    [generated, reviewSelected]
  );

  const suppliersQ = useQuery({
    queryKey: ["gr", "suppliers"],
    queryFn: () => grInvoiceAutomationApi.suppliers({ limit: 5000 }),
    staleTime: 10 * 60 * 1000,
  });
  const warehousesQ = useQuery({
    queryKey: ["gr", "warehouses"],
    queryFn: grInvoiceAutomationApi.warehouses,
    staleTime: 10 * 60 * 1000,
  });

  const searchMut = useMutation({
    mutationFn: () => grInvoiceAutomationApi.searchHeaders(filters),
    onSuccess: (rows) => {
      setHeaders(rows);
      setSelected(new Set());
      if (rows.length === 0) {
        notifications.show({
          color: "yellow",
          message: "No GRs matched the current filters",
        });
      }
    },
    onError: (e) =>
      notifications.show({ color: "red", message: apiError(e) }),
  });

  const generateMut = useMutation({
    mutationFn: () =>
      grInvoiceAutomationApi.generate([...selected], filters.excludeFresh),
    onSuccess: (data) => {
      setGenerated(data);
      if (!data.payload || data.payload.length === 0) {
        notifications.show({
          color: "yellow",
          message:
            data.filteredOut > 0
              ? `All ${data.filteredOut} selected GRs were filtered out (fresh exclusion).`
              : "Nothing was generated.",
        });
        return;
      }
      setStep(1);
      if (data.filteredOut > 0) {
        notifications.show({
          color: "yellow",
          message: `${data.filteredOut} selected GR(s) excluded (fresh items)`,
        });
      }
    },
    onError: (e) =>
      notifications.show({ color: "red", message: apiError(e) }),
  });

  const resetAll = useCallback(() => {
    setStep(0);
    setHeaders([]);
    setSelected(new Set());
    setGenerated(null);
    setFilters(DEFAULT_FILTERS());
  }, []);

  return (
    <Stack gap="lg">
      <Paper p="lg">
        <Group justify="space-between" wrap="wrap" align="center">
          <Stepper
            active={step}
            onStepClick={(s) => {
              if (s === 0) setStep(0);
              if (s === 1 && generated?.payload?.length > 0) setStep(1);
              if (s === 2 && generated?.payload?.length > 0 && canPush)
                setStep(2);
            }}
            allowNextStepsSelect={false}
            style={{ flex: 1, minWidth: 320 }}
          >
            <Stepper.Step
              label="Filter & Select"
              description="Search SAP GRs"
              icon={<IconFilter size={16} />}
            />
            <Stepper.Step
              label="Review JSON"
              description="Inspect generated payload"
              icon={<IconBraces size={16} />}
            />
            <Stepper.Step
              label="Push to Duvo"
              description="Upload to sandbox"
              icon={<IconCloudUpload size={16} />}
            />
          </Stepper>
          {generated?.payload?.length > 0 && (
            <Badge variant="light" color="indigo" size="lg">
              {reviewSelected.size} / {generated.payload.length} selected
            </Badge>
          )}
        </Group>
      </Paper>

      {step === 0 && (
        <Stack gap="md">
          <FilterPanel
            filters={filters}
            setFilters={setFilters}
            onSearch={() => searchMut.mutate()}
            onReset={() => {
              setFilters(DEFAULT_FILTERS());
              setHeaders([]);
              setSelected(new Set());
            }}
            loading={searchMut.isPending}
            suppliers={suppliersQ.data || []}
            warehouses={warehousesQ.data || []}
            suppliersLoading={suppliersQ.isLoading}
            warehousesLoading={warehousesQ.isLoading}
          />

          {(suppliersQ.error || warehousesQ.error) && (
            <Alert color="red" icon={<IconAlertTriangle size={16} />}>
              Failed to load master data from SAP HANA:{" "}
              {apiError(suppliersQ.error || warehousesQ.error)}
            </Alert>
          )}

          {headers.length === 0 ? (
            searchMut.isPending ? null : (
              <Paper>
                <Group gap="xs" c="dimmed">
                  <IconInfoCircle size={16} />
                  <Text size="sm">
                    No results yet. Adjust the filters above and click{" "}
                    <strong>Search GRs</strong>.
                  </Text>
                </Group>
              </Paper>
            )
          ) : (
            <ResultsTable
              headers={headers}
              selected={selected}
              setSelected={setSelected}
              onGenerate={() => generateMut.mutate()}
              generating={generateMut.isPending}
            />
          )}
        </Stack>
      )}

      {step === 1 && generated && (
        <JsonReview
          payload={generated.payload}
          filteredOut={generated.filteredOut}
          selectedKeys={reviewSelected}
          setSelectedKeys={setReviewSelected}
          onBack={() => setStep(0)}
          onNext={() => {
            if (!canPush) {
              notifications.show({
                color: "red",
                message:
                  "You don't have permission to push to Duvo. Ask an admin for the 'push' action.",
              });
              return;
            }
            if (reviewSelected.size === 0) {
              notifications.show({
                color: "yellow",
                message: "Select at least one GR before pushing.",
              });
              return;
            }
            setStep(2);
          }}
        />
      )}

      {step === 2 && generated && (
        <>
          {!canPush ? (
            <Alert color="red" icon={<IconAlertTriangle size={16} />}>
              You don't have permission to push to Duvo on this module. Ask an
              admin to grant the <Code>push</Code> action on this module via{" "}
              <Anchor component={Link} to="/admin/roles">
                Roles & Permissions
              </Anchor>
              .
            </Alert>
          ) : (
            <DuvoPushPanel
              payload={reviewSelectedPayload}
              totalCount={generated.payload.length}
              onBack={() => setStep(1)}
              onReset={resetAll}
            />
          )}
        </>
      )}
    </Stack>
  );
}
